import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCut as ContentCutIcon,
  Download as DownloadIcon,
  PlayArrowOutlined,
  RestoreOutlined,
  SubjectOutlined,
  UploadFileOutlined,
} from '@mui/icons-material';
import * as UpChunk from '@mux/upchunk';
import moment from 'moment';
import { api } from '../../helpers/apiUtils';
import { GefahrBestaetigung, NurAdmin } from '../../components/marketingForm';
import { TECHNIK_KONTAKT } from './texteLive';

type Mp4Status = 'preparing' | 'ready' | 'errored' | null;

interface PendingTrim {
  start_sec: number;
  end_sec: number;
  applied_at: string | null;
  applied_by_email: string | null;
}

/** In-flight Marketing upload. `ready` never appears here — a finished swap is represented by `original`. */
interface ReplacementState {
  status: 'uploading' | 'preparing' | 'errored';
  error: string | null;
  started_at: string | null;
  started_by_email: string | null;
}

/** Present only when the recording was replaced — the parked live recording, restorable. */
interface OriginalRecording {
  asset_id: string;
  playback_id: string | null;
  duration_sec: number | null;
  replaced_at: string | null;
  replaced_by_email: string | null;
}

interface Replay {
  id: number;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  duration_sec: number | null;
  playback_id: string | null;
  playback_url: string | null;
  peak_viewer_count: number;
  cta_clicks: number;
  mp4_status: Mp4Status;
  mp4_url: string | null;
  pending_trim: PendingTrim | null;
  replacement: ReplacementState | null;
  original: OriginalRecording | null;
  is_test?: boolean;
  // Themen des Sendetags aus dem Sendeplan (leer, wenn keine gepflegt sind).
  topics?: string[];
}

// Transkript (Plan 2026-10-28): GET /api/live/admin/replays/:id/transcript.
interface TranskriptSegment {
  start: number;
  end: number;
  text: string;
}
type TranskriptZustand =
  | { art: 'laedt' }
  | { art: 'ok'; segmente: TranskriptSegment[]; text: string }
  | { art: 'keins'; details: string | null }
  | { art: 'fehler'; meldung: string };

const ZEITRAUM_TAGE = [30, 90, 365] as const;
const LAENGSTER_ZEITRAUM = ZEITRAUM_TAGE[ZEITRAUM_TAGE.length - 1];
// Kennzeichen-Chips in Tabelle und Erklärsatz gleich groß, damit die Legende wie die Zeile aussieht.
const KENNZEICHEN_SX = { height: 18, fontSize: 10, fontWeight: 700 } as const;

/**
 * Aufbewahrung der ungekürzten Fassung nach einem Schnitt. Spiegelt
 * `TRIM_RETENTION_DAYS` in node-app/src/services/LiveReplayTrimService.ts — ändert sich dort der
 * Wert, muss er hier mit (die Admin-API liefert ihn nicht aus).
 */
const KUERZEN_AUFBEWAHRUNG_TAGE = 7;
const TRANSKRIPT_HINWEIS =
  'Transkript der Originalaufnahme — nach Schnitt oder Ersatzvideo können Zeitmarken und Inhalt abweichen.';

function sendungsTitel(r: Replay): string {
  return `Live vom ${moment.utc(r.started_at).format('DD.MM.YYYY')} um ${moment
    .utc(r.started_at)
    .format('HH:mm')} Uhr`;
}

const HLS_CDN = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.15/dist/hls.min.js';
const POLL_MS = 5000;

type HlsCtor = (new (
  config?: unknown,
) => {
  loadSource: (url: string) => void;
  attachMedia: (el: HTMLVideoElement) => void;
  destroy: () => void;
}) & {
  isSupported: () => boolean;
};

interface HlsGlobal {
  Hls?: HlsCtor;
}

let hlsLoadPromise: Promise<HlsCtor | null> | null = null;
function loadHls(): Promise<HlsCtor | null> {
  if (hlsLoadPromise) return hlsLoadPromise;
  hlsLoadPromise = new Promise<HlsCtor | null>((resolve) => {
    if ((window as HlsGlobal).Hls) {
      resolve((window as HlsGlobal).Hls ?? null);
      return;
    }
    const script = document.createElement('script');
    script.src = HLS_CDN;
    script.async = true;
    script.onload = () => resolve((window as HlsGlobal).Hls ?? null);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return hlsLoadPromise;
}

// Länge als Minuten:Sekunden („28:46“), ab einer Stunde Stunden:Minuten:Sekunden — vorher
// „28m 46s“, das in der schmalen Spalte umbrach (Persona-Runde 1).
function formatDuration(secs: number | null): string {
  if (secs == null || secs <= 0) return '–';
  const ganz = Math.round(secs);
  const h = Math.floor(ganz / 3600);
  const m = Math.floor((ganz % 3600) / 60);
  const s = ganz % 60;
  const zwei = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${zwei(m)}:${zwei(s)}` : `${m}:${zwei(s)}`;
}

// MM:SS for ≤ 1h, H:MM:SS otherwise. Used for the in/out timecode picker.
function formatTimecode(secs: number): string {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

function parseTimecode(raw: string): number {
  const v = (raw || '').trim();
  if (!v) return NaN;
  if (/^\d+(\.\d+)?$/.test(v)) return Number(v);
  const parts = v.split(':').map((p) => p.trim());
  if (parts.some((p) => !/^\d+$/.test(p))) return NaN;
  const nums = parts.map((p) => parseInt(p, 10));
  if (nums.length === 2) return nums[0] * 60 + nums[1];
  if (nums.length === 3) return nums[0] * 3600 + nums[1] * 60 + nums[2];
  return NaN;
}

/** Entwurf eines Zeitfelds, der sich nicht als Zeit lesen lässt (leer zählt als „kein Entwurf“). */
function zeitEntwurfUngueltig(entwurf: string | null): boolean {
  return entwurf != null && entwurf.trim() !== '' && !Number.isFinite(parseTimecode(entwurf));
}

/**
 * Zeitfeld fürs Kürzen. Getippt wird in einen Entwurf; übernommen wird erst beim Verlassen des Felds
 * (oder Enter). Vorher formatierte das Feld nach jedem Tastendruck neu — aus getipptem „01:35" wurde
 * „00:35". Der Entwurf liegt beim Aufrufer: ein ungültiger Entwurf sperrt dort das Kürzen, statt dass
 * still der letzte gültige Wert abgeschickt wird, und „Aktuelle Zeit“ verwirft ihn.
 */
const ZeitFeld: FC<{
  label: string;
  wert: number | null;
  entwurf: string | null;
  setEntwurf: (v: string | null) => void;
  onUebernehmen: (sek: number) => void;
  hilfe: string;
}> = ({ label, wert, entwurf, setEntwurf, onUebernehmen, hilfe }) => {
  const anzeige = entwurf ?? (wert == null ? '' : formatTimecode(wert));
  const ungueltig = zeitEntwurfUngueltig(entwurf);
  const uebernehmen = () => {
    if (entwurf == null) return;
    const v = parseTimecode(entwurf);
    if (Number.isFinite(v)) {
      onUebernehmen(v);
      setEntwurf(null);
    } else if (entwurf.trim() === '') {
      setEntwurf(null);
    }
  };
  return (
    <TextField
      size="small"
      label={label}
      value={anzeige}
      onChange={(e) => setEntwurf(e.target.value)}
      onBlur={uebernehmen}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      error={ungueltig}
      placeholder="MM:SS"
      helperText={ungueltig ? 'Bitte als Minuten:Sekunden eingeben, z. B. 01:35.' : hilfe}
      sx={{ flex: 1 }}
    />
  );
};

const Replays: FC = () => {
  const [replays, setReplays] = useState<Replay[]>([]);
  const anzahlTests = useMemo(() => replays.filter((r) => r.is_test).length, [replays]);
  const [tage, setTage] = useState<number>(30);
  const [transkriptFolge, setTranskriptFolge] = useState<Replay | null>(null);
  const [transkript, setTranskript] = useState<TranskriptZustand>({ art: 'laedt' });
  // Nur die Antwort der zuletzt geöffneten Folge darf den Dialog füllen (A öffnen, schnell B öffnen).
  const transkriptAbruf = useRef(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const [busyReplayId, setBusyReplayId] = useState<number | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeReplay, setActiveReplay] = useState<Replay | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsInstanceRef = useRef<{ destroy: () => void } | null>(null);

  // Trim picker state (per-modal-open). Held as seconds; rendered as MM:SS.
  const [trimStart, setTrimStart] = useState<number | null>(null);
  const [trimEnd, setTrimEnd] = useState<number | null>(null);
  const [anfangEntwurf, setAnfangEntwurf] = useState<string | null>(null);
  const [endeEntwurf, setEndeEntwurf] = useState<string | null>(null);
  const [trimSubmitting, setTrimSubmitting] = useState(false);
  // Länge laut Player — nur Rückfall, wenn die Liste `duration_sec` (noch) nicht kennt.
  const [videoLaenge, setVideoLaenge] = useState<number | null>(null);
  // Rückfrage vor dem Kürzen (GefahrBestaetigung statt window.confirm, Wirkung in Klartext).
  const [kuerzenFrage, setKuerzenFrage] = useState(false);
  const [kuerzenFehler, setKuerzenFehler] = useState<string | null>(null);

  // Replacement upload state. `uploadProgress` is 0-100 while the browser PUTs
  // chunks to Mux; the server-side 'preparing' phase afterwards is driven by
  // `activeReplay.replacement` and the poll below.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [replaceBusy, setReplaceBusy] = useState(false);
  // Live UpChunk handle, so "Upload abbrechen" can actually stop the transfer
  // instead of only clearing the DB row while the browser keeps PUTting chunks
  // into an upload nobody is tracking any more.
  const upChunkRef = useRef<{ abort: () => void } | null>(null);
  // `replaceBusy` covers the whole upload, so it cannot gate the cancel button.
  // This flag covers only the short cancel/restore requests.
  const [replaceActionBusy, setReplaceActionBusy] = useState(false);

  const fetchReplays = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ success: boolean; replays: Replay[] }>(
        '/api/live/admin/replays',
        { params: { days: tage } },
      );
      setReplays(res.data.replays || []);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setToast({
        msg:
          e?.response?.data?.error || e?.message || 'Aufzeichnungen konnten nicht geladen werden',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [tage]);

  useEffect(() => {
    fetchReplays();
  }, [fetchReplays]);

  // Poll while ANY MP4 rendition or trim is in flight — webhook-driven
  // transitions surface a few seconds after Mux finishes encoding.
  const anyReplayPreparing = useMemo(
    () => replays.some((r) => r.mp4_status === 'preparing'),
    [replays],
  );
  const anyPendingTrim = useMemo(() => replays.some((r) => r.pending_trim !== null), [replays]);
  // 'errored' is terminal — don't keep polling for it, only the two live states.
  const anyReplacementPending = useMemo(
    () =>
      replays.some(
        (r) => r.replacement?.status === 'uploading' || r.replacement?.status === 'preparing',
      ),
    [replays],
  );

  useEffect(() => {
    if (!anyReplayPreparing && !anyPendingTrim && !anyReplacementPending) return;
    const timer = window.setInterval(() => {
      fetchReplays();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [anyReplayPreparing, anyPendingTrim, anyReplacementPending, fetchReplays]);

  // Keep the open modal's `activeReplay` in sync with the freshly-fetched
  // list, so the pending_trim banner inside the dialog flips to "Schnitt
  // angewendet" without the operator closing + reopening the dialog.
  useEffect(() => {
    if (!activeReplay) return;
    const fresh = replays.find((r) => r.id === activeReplay.id);
    if (fresh && fresh !== activeReplay) setActiveReplay(fresh);
  }, [replays, activeReplay]);

  const openReplay = (r: Replay) => {
    if (!r.playback_url) {
      setToast({ msg: 'Diese Aufzeichnung ist noch nicht abrufbar.', severity: 'error' });
      return;
    }
    setActiveReplay(r);
    setTrimStart(null);
    setTrimEnd(null);
    setAnfangEntwurf(null);
    setEndeEntwurf(null);
    setVideoLaenge(null);
    setKuerzenFrage(false);
    setKuerzenFehler(null);
    setUploadProgress(null);
    setDialogOpen(true);
  };

  const closeReplay = () => {
    setDialogOpen(false);
    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.destroy();
      hlsInstanceRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
    }
    setActiveReplay(null);
  };

  useEffect(() => {
    if (!dialogOpen || !activeReplay?.playback_url) return;
    let cancelled = false;
    (async () => {
      const HlsClass = await loadHls();
      if (cancelled || !videoRef.current || !activeReplay.playback_url) return;
      const video = videoRef.current;
      if (HlsClass && HlsClass.isSupported()) {
        const hls = new HlsClass();
        hls.loadSource(activeReplay.playback_url);
        hls.attachMedia(video);
        hlsInstanceRef.current = hls;
      } else {
        video.src = activeReplay.playback_url;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, activeReplay]);

  const enableReplayDownload = async (replay: Replay) => {
    setBusyReplayId(replay.id);
    try {
      await api.post(`/api/live/admin/replays/${replay.id}/enable-download`);
      setReplays((prev) =>
        prev.map((r) => (r.id === replay.id ? { ...r, mp4_status: 'preparing' } : r)),
      );
      setToast({
        msg: 'Download-Datei wird erstellt — fertig in 1–3 Minuten.',
        severity: 'success',
      });
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setToast({
        msg:
          e?.response?.data?.error || e?.message || 'Download-Datei konnte nicht erstellt werden',
        severity: 'error',
      });
    } finally {
      setBusyReplayId(null);
    }
  };

  // --- Replay-Video ersetzen ------------------------------------------------
  // The file goes browser → Mux directly (Direct Upload); our backend only
  // hands out the signed URL. UpChunk splits it into chunks and retries each
  // one on its own, so a blip on a 3 GB studio export doesn't restart the
  // whole upload. The swap into live_sessions happens server-side afterwards
  // (webhook, or the self-heal poll behind GET /admin/replays).
  const handleReplaceFile = async (replay: Replay, file: File) => {
    const confirmed = window.confirm(
      `Video für die Sendung vom ${moment.utc(replay.started_at).format('DD.MM.YYYY')} ersetzen?\n\n` +
        `Datei: ${file.name} (${(file.size / 1024 / 1024).toFixed(0)} MB)\n\n` +
        'Die Datei ersetzt die Aufzeichnung im Archiv auf live.finanztrends.de. Die ' +
        'ursprüngliche Aufzeichnung bleibt gespeichert und lässt sich hier jederzeit mit ' +
        '„Original wiederherstellen“ zurückholen.',
    );
    if (!confirmed) return;

    // Tracks whether THIS attempt actually reserved the upload slot. The catch
    // below must never cancel a replacement it doesn't own: `replace-upload-url`
    // 409s with `replacement_already_pending` when another admin or tab holds
    // the slot, and cancelling on that path would kill their upload and delete
    // their Mux asset.
    let reserved = false;
    // Set when the user cancels mid-upload — suppresses the error toast for the
    // abort() the cancel triggers, which surfaces as a normal UpChunk error.
    let abortedByUser = false;
    setReplaceBusy(true);
    setUploadProgress(0);
    try {
      const createRes = await api.post<{ success: boolean; upload_url: string }>(
        `/api/live/admin/replays/${replay.id}/replace-upload-url`,
        { cors_origin: window.location.origin },
      );
      const uploadUrl = createRes.data.upload_url;
      if (!uploadUrl) throw new Error('keine upload_url vom Backend');
      reserved = true;

      await new Promise<void>((resolve, reject) => {
        const upload = UpChunk.createUpload({
          endpoint: uploadUrl,
          file,
          // dynamicChunkSize adapts to the measured uplink — the studio
          // connection is the slow leg and varies a lot between sessions.
          dynamicChunkSize: true,
        });
        // Keep the handle so handleCancelReplacement can abort() the transfer.
        upChunkRef.current = {
          abort: () => {
            abortedByUser = true;
            upload.abort();
            reject(new Error('Upload abgebrochen'));
          },
        };
        upload.on('progress', (evt) => {
          setUploadProgress(Math.round((evt.detail as number) || 0));
        });
        upload.on('error', (evt) => {
          const detail = evt.detail as { message?: string } | undefined;
          reject(new Error(detail?.message || 'Upload fehlgeschlagen'));
        });
        upload.on('success', () => resolve());
      });

      setUploadProgress(100);
      setToast({
        msg: 'Hochgeladen — das Video wird jetzt aufbereitet und ersetzt die Aufzeichnung danach von selbst.',
        severity: 'success',
      });
      await fetchReplays();
    } catch (err) {
      if (!abortedByUser) {
        const e = err as { response?: { data?: { error?: string } }; message?: string };
        setToast({
          msg: e?.response?.data?.error || e?.message || 'Upload fehlgeschlagen',
          severity: 'error',
        });
      }
      // Release the slot ONLY if this attempt reserved it, so the operator can
      // retry immediately instead of hitting replacement_already_pending.
      // handleCancelReplacement already released it on the user-abort path.
      if (reserved && !abortedByUser) {
        try {
          await api.post(`/api/live/admin/replays/${replay.id}/cancel-replacement`);
        } catch {
          /* best effort — the self-heal poll resolves it otherwise */
        }
      }
      await fetchReplays();
    } finally {
      upChunkRef.current = null;
      setReplaceBusy(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRestoreOriginal = async (replay: Replay) => {
    const confirmed = window.confirm(
      'Original-Aufzeichnung wiederherstellen?\n\n' +
        'Das hochgeladene Video wird dabei gelöscht und das Archiv zeigt wieder die ' +
        'ursprüngliche Aufzeichnung. Die Download-Datei muss danach neu erstellt werden.',
    );
    if (!confirmed) return;
    setReplaceActionBusy(true);
    try {
      await api.post(`/api/live/admin/replays/${replay.id}/restore-original`);
      setToast({ msg: 'Original-Aufzeichnung wiederhergestellt.', severity: 'success' });
      await fetchReplays();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      const code = e?.response?.data?.error;
      setToast({
        msg:
          code === 'original_unavailable'
            ? `Das Original ist beim Videodienst nicht mehr vorhanden — Wiederherstellen nicht möglich. Bitte ${TECHNIK_KONTAKT} fragen.`
            : code || e?.message || 'Wiederherstellen fehlgeschlagen',
        severity: 'error',
      });
    } finally {
      setReplaceActionBusy(false);
    }
  };

  const handleCancelReplacement = async (replay: Replay) => {
    // Stop the browser transfer FIRST. Clearing the DB row while chunks keep
    // flowing would leave Mux assembling an asset that no webhook can match any
    // more — an untracked, silently billed orphan.
    upChunkRef.current?.abort();
    setReplaceActionBusy(true);
    try {
      await api.post(`/api/live/admin/replays/${replay.id}/cancel-replacement`);
      setToast({ msg: 'Upload abgebrochen.', severity: 'success' });
      await fetchReplays();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setToast({
        msg: e?.response?.data?.error || e?.message || 'Abbrechen fehlgeschlagen',
        severity: 'error',
      });
    } finally {
      setReplaceActionBusy(false);
    }
  };

  // True while the open replay has an upload in flight. Gates the trim controls
  // too — both write recording_video_id, so trimming an asset that a landing
  // replacement is about to supersede just burns a Mux clip.
  const replacementInFlight =
    activeReplay?.replacement?.status === 'uploading' ||
    activeReplay?.replacement?.status === 'preparing' ||
    uploadProgress !== null;

  const captureCurrentTime = (slot: 'start' | 'end') => {
    const v = videoRef.current;
    if (!v) {
      setToast({ msg: 'Player noch nicht bereit.', severity: 'error' });
      return;
    }
    const t = Math.max(0, Math.floor(v.currentTime));
    if (slot === 'start') {
      setTrimStart(t);
      setAnfangEntwurf(null);
    } else {
      setTrimEnd(t);
      setEndeEntwurf(null);
    }
  };

  // Ende ist im Backend Pflicht (`POST /admin/replays/:id/clip` antwortet ohne `endSec` mit 400
  // invalid_timecodes; nur der ai-prod-Pfad kennt `endSec: null`). Deshalb ist das Ende mit der vollen
  // Länge vorbelegt und der Anfang mit 0 — man ändert nur die Seite, an der etwas weg soll.
  const volleLaenge: number | null =
    activeReplay?.duration_sec != null && activeReplay.duration_sec > 0
      ? Math.floor(activeReplay.duration_sec)
      : videoLaenge;
  const kuerzenStart = trimStart ?? 0;
  const kuerzenEnde = trimEnd ?? volleLaenge;
  const nichtsZuKuerzen =
    volleLaenge != null &&
    kuerzenEnde != null &&
    kuerzenStart < 1 &&
    kuerzenEnde >= volleLaenge - 1;
  const zeitUngueltig = zeitEntwurfUngueltig(anfangEntwurf) || zeitEntwurfUngueltig(endeEntwurf);

  const submitTrim = () => {
    if (!activeReplay || zeitUngueltig) return;
    if (kuerzenEnde == null) {
      setToast({ msg: 'Bitte das Ende setzen.', severity: 'error' });
      return;
    }
    if (kuerzenEnde - kuerzenStart < 1) {
      setToast({
        msg: 'Das Ende muss mindestens 1 Sekunde nach dem Anfang liegen.',
        severity: 'error',
      });
      return;
    }
    setKuerzenFehler(null);
    setKuerzenFrage(true);
  };

  const kuerzenFehlerText = (code: string | undefined, fallback: string): string => {
    switch (code) {
      case 'trim_already_pending':
        return 'Es wird schon eine Kürzung verarbeitet — bitte 1–3 Minuten warten.';
      case 'replacement_already_pending':
        return 'Gerade wird ein Ersatzvideo hochgeladen oder aufbereitet — erst danach kürzen.';
      case 'end_exceeds_source':
        return 'Das Ende liegt hinter dem Schluss der Aufzeichnung.';
      case 'duration_too_short':
        return 'Das gekürzte Stück wäre kürzer als 1 Sekunde.';
      case 'invalid_timecodes':
        return 'Anfang oder Ende ist keine gültige Zeit (Form MM:SS).';
      default:
        return code || fallback;
    }
  };

  const kuerzenAusfuehren = async () => {
    if (!activeReplay || kuerzenEnde == null) return;
    setTrimSubmitting(true);
    setKuerzenFehler(null);
    try {
      const res = await api.post<{ success: boolean; status: string }>(
        `/api/live/admin/replays/${activeReplay.id}/clip`,
        { startSec: kuerzenStart, endSec: kuerzenEnde },
      );
      setKuerzenFrage(false);
      setToast({
        msg:
          res.data.status === 'applied'
            ? 'Aufzeichnung gekürzt.'
            : 'Kürzung läuft — fertig in 1–3 Minuten.',
        severity: 'success',
      });
      setTrimStart(null);
      setTrimEnd(null);
      setAnfangEntwurf(null);
      setEndeEntwurf(null);
      fetchReplays();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setKuerzenFehler(
        kuerzenFehlerText(e?.response?.data?.error, e?.message || 'Kürzen fehlgeschlagen'),
      );
    } finally {
      setTrimSubmitting(false);
    }
  };

  // --- Transkript -----------------------------------------------------------
  // 404 = Folge noch nicht verarbeitet / nicht im Feed (freundlicher Hinweis); 503 = Quelle nicht erreichbar —
  // das ist ein Fehler und darf nie als „kein Transkript“ erscheinen.
  const oeffneTranskript = async (r: Replay) => {
    const abruf = ++transkriptAbruf.current;
    setTranskriptFolge(r);
    setTranskript({ art: 'laedt' });
    try {
      const res = await api.get<{
        success: boolean;
        data: { segments: TranskriptSegment[]; text: string };
      }>(`/api/live/admin/replays/${r.id}/transcript`);
      const segmente = Array.isArray(res.data?.data?.segments) ? res.data.data.segments : [];
      const text =
        typeof res.data?.data?.text === 'string' && res.data.data.text
          ? res.data.data.text
          : segmente.map((s) => s.text).join(' ');
      if (abruf !== transkriptAbruf.current) return;
      setTranskript({ art: 'ok', segmente, text });
    } catch (err) {
      if (abruf !== transkriptAbruf.current) return;
      const e = err as {
        response?: { status?: number; data?: { error?: string; details?: unknown } };
        message?: string;
      };
      const status = e?.response?.status;
      const details =
        typeof e?.response?.data?.details === 'string' ? e.response.data.details : null;
      if (status === 404) {
        setTranskript({ art: 'keins', details });
      } else {
        setTranskript({
          art: 'fehler',
          meldung:
            details ||
            e?.response?.data?.error ||
            e?.message ||
            'Transkript konnte nicht geladen werden.',
        });
      }
    }
  };

  const transkriptAlsText = (r: Replay, segmente: TranskriptSegment[]): string =>
    [
      `Kursgespräch — ${sendungsTitel(r)}`,
      TRANSKRIPT_HINWEIS,
      '',
      ...segmente.map((s) => `[${formatTimecode(s.start)}] ${s.text}`),
      '',
    ].join('\n');

  const kopiereTranskript = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ msg: 'Text kopiert.', severity: 'success' });
    } catch {
      // Ohne Clipboard-API (http, alte Browser): unsichtbares Textfeld + execCommand.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      setToast(
        ok
          ? { msg: 'Text kopiert.', severity: 'success' }
          : { msg: 'Kopieren hat nicht geklappt — Text bitte markieren.', severity: 'error' },
      );
    }
  };

  const ladeTranskriptHerunter = (r: Replay, segmente: TranskriptSegment[]) => {
    const blob = new Blob([transkriptAlsText(r, segmente)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transkript_${moment.utc(r.started_at).format('YYYY-MM-DD_HHmm')}_kursgespraech-live.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const renderMp4Cell = (r: Replay) => {
    if (r.mp4_status === 'ready' && r.mp4_url) {
      return (
        <Tooltip title="Videodatei (MP4) herunterladen">
          <Button
            size="small"
            component="a"
            href={r.mp4_url}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            startIcon={<DownloadIcon fontSize="small" />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Herunterladen
          </Button>
        </Tooltip>
      );
    }
    if (r.mp4_status === 'preparing') {
      return (
        <Tooltip title="Download-Datei wird erstellt (1–3 Minuten)">
          <CircularProgress size={16} />
        </Tooltip>
      );
    }
    return (
      <Tooltip
        title={
          r.mp4_status === 'errored'
            ? 'Der letzte Versuch ist fehlgeschlagen — bitte erneut versuchen.'
            : r.pending_trim !== null
              ? 'Erst die laufende Kürzung abwarten.'
              : 'Erstellt eine Videodatei (MP4) dieser Aufzeichnung zum Herunterladen, z. B. zum Weitergeben. Dauert 1–3 Minuten, einmal je Aufzeichnung.'
        }
      >
        <span>
          <Button
            size="small"
            variant={r.mp4_status === 'errored' ? 'outlined' : 'text'}
            color={r.mp4_status === 'errored' ? 'error' : 'primary'}
            disabled={!r.playback_id || busyReplayId === r.id || r.pending_trim !== null}
            onClick={() => enableReplayDownload(r)}
          >
            {busyReplayId === r.id ? '…' : 'Download-Datei erstellen'}
          </Button>
        </span>
      </Tooltip>
    );
  };

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Zeitraum"
          value={tage}
          onChange={(e) => setTage(Number(e.target.value))}
          sx={{ minWidth: 170 }}
        >
          {ZEITRAUM_TAGE.map((d) => (
            <MenuItem key={d} value={d}>
              Letzte {d} Tage
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="textSecondary">
          {replays.length} Aufzeichnung{replays.length === 1 ? '' : 'en'}
          {anzahlTests > 0 && `, davon ${anzahlTests} Test${anzahlTests === 1 ? '' : 's'}`}
        </Typography>
      </Stack>
      <Typography variant="body2" color="textSecondary" mb={1}>
        Alle Aufzeichnungen der letzten {tage} Tage, auch Tests. „Ansehen &amp; kürzen“ öffnet die
        Aufzeichnung; dort lässt sie sich auch kürzen oder durch ein eigenes Video ersetzen.
        „Transkript“ zeigt den gesprochenen Text zum Lesen, Kopieren und Herunterladen.
        „Download-Datei erstellen“ macht aus einer Aufzeichnung eine Videodatei zum Herunterladen.
      </Typography>
      {/* Wahr nach GET /api/live/replays (routes/live.ts): testFilterJeSession, mind. 5 Minuten
          Sendefenster, LIMIT 20. is_test der Admin-Liste ist die Negation desselben Filters. */}
      <Typography variant="body2" color="textSecondary" mb={2}>
        <strong>Was Zuschauer sehen:</strong> Das Archiv auf live.finanztrends.de zeigt die neuesten
        20 echten Sendungen ab 5 Minuten Länge.{' '}
        <Chip label="TEST" size="small" color="warning" sx={KENNZEICHEN_SX} /> heißt: im Sendeplan
        als Test geführt — Zuschauer sehen diese Aufzeichnung im Archiv nicht.{' '}
        <Chip label="ERSETZT" size="small" color="info" sx={KENNZEICHEN_SX} /> heißt: im Archiv
        läuft das hochgeladene eigene Video statt der Live-Aufzeichnung; das Original ist aufgehoben
        und lässt sich in der Ansicht wiederherstellen.
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Fertig produzierte Sendungsvideos, die statt einer Live-Sendung laufen, stehen im Reiter
        „Videos“, nicht hier.
      </Typography>

      {loading && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      <TableContainer
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: 1,
          border: '1px solid #e0e0e0',
          overflowX: 'auto',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Sendung
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Dauer
              </TableCell>
              <TableCell
                align="right"
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
              >
                <Tooltip
                  arrow
                  placement="top"
                  title="Die meisten Zuschauer, die in einem Moment gleichzeitig zugesehen haben — wie eine Momentaufnahme. Nicht alle, die im Lauf der Sendung irgendwann dabei waren."
                >
                  <span style={{ cursor: 'help' }}>Zuschauer (Höchststand)</span>
                </Tooltip>
              </TableCell>
              <TableCell
                align="right"
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
              >
                <Tooltip
                  arrow
                  placement="top"
                  title="Klicks auf Einblendungen während der Sendung plus Klicks auf den Knopf im Werbevideo danach"
                >
                  <span style={{ cursor: 'help' }}>Klicks</span>
                </Tooltip>
              </TableCell>
              <TableCell
                align="center"
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
              >
                Download-Datei
              </TableCell>
              <TableCell
                align="center"
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
              >
                Ansehen
              </TableCell>
              <TableCell
                align="center"
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
              >
                Transkript
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {replays.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} sx={{ color: 'text.secondary' }}>
                  {tage < LAENGSTER_ZEITRAUM ? (
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <span>
                        Keine Aufzeichnung in den letzten {tage} Tagen — Zeitraum oben vergrößern.
                      </span>
                      <Button size="small" onClick={() => setTage(LAENGSTER_ZEITRAUM)}>
                        Letzte {LAENGSTER_ZEITRAUM} Tage zeigen
                      </Button>
                    </Stack>
                  ) : (
                    `Keine Aufzeichnung in den letzten ${tage} Tagen.`
                  )}
                </TableCell>
              </TableRow>
            )}
            {replays.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell onClick={() => openReplay(r)} sx={{ cursor: 'pointer' }}>
                  Live vom {moment.utc(r.started_at).format('DD.MM.YYYY')} um{' '}
                  {moment.utc(r.started_at).format('HH:mm')} Uhr
                  {r.is_test && (
                    <Tooltip
                      arrow
                      placement="top"
                      title="Im Sendeplan als Test geführt. Zuschauer sehen diese Aufzeichnung im Archiv auf live.finanztrends.de nicht — hier steht sie nur zum Prüfen."
                    >
                      <Chip
                        label="TEST"
                        size="small"
                        color="warning"
                        sx={{ ...KENNZEICHEN_SX, ml: 1, cursor: 'help' }}
                      />
                    </Tooltip>
                  )}
                  {r.original && (
                    <Tooltip
                      arrow
                      placement="top"
                      title={`Eigenes Video hochgeladen${
                        r.original.replaced_by_email ? ` von ${r.original.replaced_by_email}` : ''
                      }${
                        r.original.replaced_at
                          ? ` am ${moment(r.original.replaced_at).format('DD.MM.YYYY HH:mm')}`
                          : ''
                      }. Zuschauer sehen im Archiv dieses Video statt der Live-Aufzeichnung. Die ursprüngliche Aufzeichnung ist aufgehoben und lässt sich in der Ansicht wiederherstellen.`}
                    >
                      <Chip
                        label="ERSETZT"
                        size="small"
                        color="info"
                        sx={{ ...KENNZEICHEN_SX, ml: 1, cursor: 'help' }}
                      />
                    </Tooltip>
                  )}
                  {(r.topics ?? []).length > 0 && (
                    <Tooltip
                      arrow
                      placement="top"
                      title="Themen dieses Sendetags, wie sie im Sendeplan unter „Heute im Stream“ eingetragen waren"
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                        sx={{ cursor: 'help', maxWidth: 420 }}
                      >
                        Themen: {(r.topics ?? []).join(' · ')}
                      </Typography>
                    </Tooltip>
                  )}
                  {r.pending_trim && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      sx={{ fontStyle: 'italic' }}
                    >
                      Kürzung wird verarbeitet ({formatTimecode(r.pending_trim.start_sec)} –{' '}
                      {formatTimecode(r.pending_trim.end_sec)})
                    </Typography>
                  )}
                  {r.replacement && r.replacement.status !== 'errored' && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      sx={{ fontStyle: 'italic' }}
                    >
                      {r.replacement.status === 'uploading'
                        ? 'Video-Upload läuft …'
                        : 'Hochgeladenes Video wird verarbeitet …'}
                    </Typography>
                  )}
                  {r.replacement?.status === 'errored' && (
                    <Typography
                      variant="caption"
                      color="error"
                      display="block"
                      sx={{ fontStyle: 'italic' }}
                    >
                      Video-Upload fehlgeschlagen
                    </Typography>
                  )}
                </TableCell>
                <TableCell
                  onClick={() => openReplay(r)}
                  sx={{
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatDuration(r.duration_sec ?? r.duration_seconds)}
                </TableCell>
                <TableCell align="right" onClick={() => openReplay(r)} sx={{ cursor: 'pointer' }}>
                  {r.peak_viewer_count || '–'}
                </TableCell>
                <TableCell align="right" onClick={() => openReplay(r)} sx={{ cursor: 'pointer' }}>
                  {r.cta_clicks}
                </TableCell>
                <TableCell align="center">{renderMp4Cell(r)}</TableCell>
                <TableCell align="center">
                  {/* Text statt ▷ (Persona-Runde 1: Symbol ohne Beschriftung). */}
                  <Tooltip
                    title={
                      r.playback_url
                        ? 'Öffnet die Aufzeichnung — dort lässt sie sich auch kürzen oder ersetzen'
                        : 'Aufzeichnung wird noch aufbereitet'
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        startIcon={<PlayArrowOutlined />}
                        onClick={() => openReplay(r)}
                        disabled={!r.playback_url}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Ansehen &amp; kürzen
                      </Button>
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    startIcon={<SubjectOutlined />}
                    onClick={() => oeffneTranskript(r)}
                  >
                    Transkript
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeReplay} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          {activeReplay
            ? `Live vom ${moment.utc(activeReplay.started_at).format('DD.MM.YYYY HH:mm')} Uhr`
            : ''}
          <IconButton onClick={closeReplay} size="small" aria-label="Schließen">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {activeReplay?.playback_url ? (
            <video
              ref={videoRef}
              controls
              autoPlay
              playsInline
              onLoadedMetadata={(e) => {
                const d = e.currentTarget.duration;
                setVideoLaenge(Number.isFinite(d) && d > 0 ? Math.floor(d) : null);
              }}
              style={{ width: '100%', maxHeight: '60vh', backgroundColor: '#000' }}
            />
          ) : (
            <Alert severity="info">Diese Aufzeichnung lässt sich noch nicht abspielen.</Alert>
          )}

          {activeReplay && (
            <Box
              mt={2}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
                alignItems: 'start',
              }}
            >
              {/* Kürzen und Ersetzen nebeneinander, jedes mit seiner eigenen Wahrheit über das
                  Original: Kürzen parkt die ungekürzte Fassung 7 Tage (LiveReplayTrimService,
                  TRIM_RETENTION_DAYS) ohne Knopf zum Zurückholen; Ersetzen behält das Original
                  dauerhaft in original_* mit „Original wiederherstellen“
                  (LiveReplayReplacementService). Bis 28.10. stand nur unter „Ersetzen“ „Original
                  bleibt erhalten“ — Personas lasen das als für beide geltend. */}
              {activeReplay.playback_url && (
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Aufzeichnung kürzen
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" mb={1.5}>
                    Schneidet am Anfang und/oder am Ende etwas weg. Im Player zur Stelle springen,
                    dann „Aktuelle Zeit“ klicken. Die Schnittstelle kann 1–2 Sekunden abweichen.
                  </Typography>

                  {activeReplay.pending_trim ? (
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      Kürzung wird verarbeitet:{' '}
                      {formatTimecode(activeReplay.pending_trim.start_sec)} –{' '}
                      {formatTimecode(activeReplay.pending_trim.end_sec)}. Dauert meist 1–3 Minuten,
                      danach zeigt das Archiv die gekürzte Fassung von selbst. Eine weitere Kürzung
                      geht erst danach.
                    </Alert>
                  ) : (
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <ZeitFeld
                          label="Anfang"
                          wert={kuerzenStart}
                          entwurf={anfangEntwurf}
                          setEntwurf={setAnfangEntwurf}
                          onUebernehmen={setTrimStart}
                          hilfe="Ab hier bleibt es stehen. 00:00 = vorne nichts weg."
                        />
                        <Button
                          size="small"
                          sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
                          onClick={() => captureCurrentTime('start')}
                        >
                          Aktuelle Zeit
                        </Button>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <ZeitFeld
                          label="Ende"
                          wert={kuerzenEnde}
                          entwurf={endeEntwurf}
                          setEntwurf={setEndeEntwurf}
                          onUebernehmen={setTrimEnd}
                          hilfe={
                            volleLaenge == null
                              ? 'Länge noch unbekannt — Ende bitte selbst setzen.'
                              : `Vorbelegt mit dem Schluss der Aufzeichnung (${formatTimecode(volleLaenge)}). Nur ändern, wenn auch hinten etwas weg soll.`
                          }
                        />
                        <Button
                          size="small"
                          sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
                          onClick={() => captureCurrentTime('end')}
                        >
                          Aktuelle Zeit
                        </Button>
                      </Stack>
                      <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
                        <Typography variant="caption" component="div">
                          <strong>Dauerhaft:</strong> Zuschauer sehen im Archiv danach nur noch das
                          gekürzte Stück. Die ungekürzte Fassung wird {KUERZEN_AUFBEWAHRUNG_TAGE}{' '}
                          Tage aufbewahrt — zurückholen geht nur über {TECHNIK_KONTAKT}, hier gibt
                          es dafür keinen Knopf.
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                          Stille am Anfang und den Verkaufsteil am Ende schneidet das System nach
                          jeder Sendung meist selbst weg (nach dem YouTube-Upload, gegen 11 Uhr). Am
                          Sendetag deshalb erst danach kürzen — ein eigener Schnitt vorher
                          verhindert den automatischen.
                        </Typography>
                      </Alert>
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          replacementInFlight
                            ? 'Erst den laufenden Video-Upload abwarten — er ersetzt die Aufzeichnung.'
                            : zeitUngueltig
                              ? 'Erst die rot markierte Zeit korrigieren.'
                              : nichtsZuKuerzen
                                ? 'Noch nichts zu kürzen — Anfang oder Ende verschieben.'
                                : ''
                        }
                      >
                        <span>
                          <Button
                            variant="contained"
                            color="warning"
                            startIcon={<ContentCutIcon />}
                            disabled={
                              trimSubmitting ||
                              replacementInFlight ||
                              kuerzenEnde == null ||
                              zeitUngueltig ||
                              nichtsZuKuerzen
                            }
                            onClick={submitTrim}
                          >
                            {trimSubmitting ? 'Wird gekürzt …' : 'Aufzeichnung kürzen …'}
                          </Button>
                        </span>
                      </Tooltip>
                      {/* Grauer Knopf ohne sichtbaren Grund wirkte kaputt (Persona-Runde 1) — der
                          Grund steht deshalb direkt darunter, nicht nur im Tooltip. */}
                      {!trimSubmitting &&
                        (replacementInFlight || zeitUngueltig || nichtsZuKuerzen) && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {replacementInFlight
                              ? 'Noch gesperrt: erst den laufenden Video-Upload abwarten.'
                              : zeitUngueltig
                                ? 'Noch gesperrt: erst die rot markierte Zeit korrigieren.'
                                : 'Noch gesperrt: erst Anfang oder Ende verschieben — dann lässt sich kürzen.'}
                          </Typography>
                        )}
                    </Stack>
                  )}
                </Box>
              )}

              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Video ersetzen
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" mb={1.5}>
                  Zeigt eine eigene Videodatei statt der Aufzeichnung — wenn die Aufzeichnung
                  unbrauchbar ist (Ton weg, Absturz) oder außerhalb geschnitten wurde.{' '}
                  <strong>Anders als beim Kürzen</strong> bleibt die ursprüngliche Aufzeichnung
                  dauerhaft gespeichert und lässt sich hier jederzeit mit „Original
                  wiederherstellen“ zurückholen. Die Download-Datei danach neu erstellen.
                </Typography>

                {activeReplay.replacement?.status === 'errored' && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    Letzter Upload fehlgeschlagen
                    {activeReplay.replacement.error ? `: ${activeReplay.replacement.error}` : ''}.
                    Ein neuer Versuch ist möglich — die Aufzeichnung wurde nicht verändert.
                  </Alert>
                )}

                {replacementInFlight ? (
                  <Box>
                    <Alert severity="info" sx={{ mb: 1.5 }}>
                      {/* uploadProgress is the local truth while the browser is still
                          PUTting — the row's status only catches up on the next poll. */}
                      {uploadProgress !== null || activeReplay.replacement?.status === 'uploading'
                        ? 'Video wird hochgeladen — Fenster bitte offen lassen.'
                        : 'Das hochgeladene Video wird aufbereitet (meist 1–3 Minuten). Danach ersetzt es die Aufzeichnung von selbst.'}
                    </Alert>
                    {uploadProgress !== null && (
                      <Box mb={1.5}>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                        <Typography variant="caption" color="textSecondary">
                          {uploadProgress}% hochgeladen
                        </Typography>
                      </Box>
                    )}
                    {/* Deliberately NOT gated on `replaceBusy` — that is true for the
                        whole upload, which would make this button useless exactly when
                        it is needed (wrong 3 GB file picked). It aborts the transfer
                        and releases the slot. */}
                    <Button
                      size="small"
                      color="inherit"
                      disabled={replaceActionBusy}
                      onClick={() => handleCancelReplacement(activeReplay)}
                    >
                      Upload abbrechen
                    </Button>
                  </Box>
                ) : (
                  <Stack spacing={1.5} alignItems="flex-start">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleReplaceFile(activeReplay, file);
                      }}
                    />
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        activeReplay.pending_trim
                          ? 'Erst die laufende Kürzung abwarten.'
                          : 'MP4/MOV, beliebige Länge. Der Upload läuft in Teilstücken und übersteht kurze Netzaussetzer.'
                      }
                    >
                      <span>
                        <Button
                          variant="outlined"
                          startIcon={<UploadFileOutlined />}
                          disabled={
                            replaceBusy || replaceActionBusy || activeReplay.pending_trim !== null
                          }
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Video auswählen
                        </Button>
                      </span>
                    </Tooltip>
                    {activeReplay.original && (
                      <Stack spacing={0.5} alignItems="flex-start">
                        <Button
                          variant="text"
                          color="primary"
                          startIcon={<RestoreOutlined />}
                          disabled={replaceBusy || replaceActionBusy}
                          onClick={() => handleRestoreOriginal(activeReplay)}
                        >
                          Original wiederherstellen
                        </Button>
                        <Typography variant="caption" color="textSecondary">
                          {/* replaced_at is written by sequelize NOW() = real UTC, so plain
                              moment() renders it correctly in Berlin. Do NOT copy this to
                              started_at: that column holds Berlin wallclock bytes and needs
                              moment.utc(). Coordinate system is per-COLUMN — see CLAUDE.md. */}
                          Ersetzt
                          {activeReplay.original.replaced_at
                            ? ` am ${moment(activeReplay.original.replaced_at).format('DD.MM.YYYY HH:mm')}`
                            : ''}
                          {activeReplay.original.replaced_by_email
                            ? ` von ${activeReplay.original.replaced_by_email}`
                            : ''}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                )}
              </Box>
            </Box>
          )}

          {activeReplay && (
            <Typography variant="caption" color="textSecondary" display="block" mt={2}>
              Für beide gilt: YouTube-Video, Finanztrends-Artikel und Transkript entstehen einmal
              kurz nach der Sendung und bleiben, wie sie sind — Kürzen oder Ersetzen ändert daran
              nichts. Wer dort etwas ändern will: {TECHNIK_KONTAKT} fragen.
            </Typography>
          )}

          <NurAdmin>
            <Typography variant="caption" color="textSecondary" display="block" mt={1}>
              Technik: Schnitt per Mux-Clip, keyframe-genau (±~2 s); das ersetzte Asset liegt{' '}
              {KUERZEN_AUFBEWAHRUNG_TAGE} Tage in live_mux_asset_retention, Rücknahme per POST
              /admin/replays/:id/restore-trim.
            </Typography>
          </NurAdmin>
        </DialogContent>
      </Dialog>

      <GefahrBestaetigung
        open={kuerzenFrage}
        titel="Aufzeichnung wirklich kürzen?"
        wirkung={
          <>
            Die Aufzeichnung wird auf{' '}
            <strong>
              {formatTimecode(kuerzenStart)} –{' '}
              {kuerzenEnde == null ? '' : formatTimecode(kuerzenEnde)}
            </strong>{' '}
            gekürzt
            {kuerzenEnde != null
              ? ` (neue Länge ${formatTimecode(Math.max(0, kuerzenEnde - kuerzenStart))})`
              : ''}
            . Alles davor und danach sehen Zuschauer im Archiv nicht mehr.
            <br />
            <br />• Die Download-Datei wird danach automatisch neu erstellt (1–3 Minuten).
            <br />• YouTube-Video, Finanztrends-Artikel und Transkript bleiben, wie sie sind. Wer
            dort etwas ändern will: {TECHNIK_KONTAKT} fragen.
            <br />• Die ungekürzte Fassung wird {KUERZEN_AUFBEWAHRUNG_TAGE} Tage aufbewahrt und ist
            danach endgültig weg. Zurückholen geht nur über {TECHNIK_KONTAKT} — hier gibt es dafür
            keinen Knopf.
          </>
        }
        knopfText="Jetzt kürzen"
        laeuft={trimSubmitting}
        fehler={kuerzenFehler}
        onBestaetigen={() => void kuerzenAusfuehren()}
        onAbbrechen={() => {
          setKuerzenFrage(false);
          setKuerzenFehler(null);
        }}
      />

      <Dialog
        open={!!transkriptFolge}
        onClose={() => setTranskriptFolge(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
        >
          <span>Transkript — {transkriptFolge ? sendungsTitel(transkriptFolge) : ''}</span>
          <IconButton size="small" onClick={() => setTranskriptFolge(null)} aria-label="Schließen">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            {TRANSKRIPT_HINWEIS}
          </Alert>
          {transkript.art === 'laedt' && (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress size={24} />
            </Box>
          )}
          {transkript.art === 'keins' && (
            <Typography variant="body2" color="textSecondary">
              Für diese Folge gibt es noch kein Transkript. Es entsteht automatisch einige Zeit nach
              der Sendung — später noch einmal öffnen.
              {transkript.details ? ` (${transkript.details})` : ''}
            </Typography>
          )}
          {transkript.art === 'fehler' && (
            <Alert severity="error">
              Das Transkript konnte gerade nicht geladen werden: {transkript.meldung}
            </Alert>
          )}
          {transkript.art === 'ok' && transkript.segmente.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Das Transkript dieser Folge ist leer.
            </Typography>
          )}
          {transkript.art === 'ok' && transkript.segmente.length > 0 && (
            <Box component="dl" sx={{ m: 0 }}>
              {transkript.segmente.map((seg, i) => (
                <Box
                  key={i}
                  sx={{ display: 'flex', gap: 1.5, py: 0.5, borderBottom: '1px solid #f0f1f3' }}
                >
                  <Box
                    component="dt"
                    sx={{
                      flex: 'none',
                      width: 64,
                      fontVariantNumeric: 'tabular-nums',
                      color: 'text.secondary',
                      fontSize: 13,
                      pt: '2px',
                    }}
                  >
                    {formatTimecode(seg.start)}
                  </Box>
                  <Box component="dd" sx={{ m: 0, fontSize: 14, lineHeight: 1.5 }}>
                    {seg.text}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            disabled={transkript.art !== 'ok' || transkript.segmente.length === 0}
            onClick={() => {
              if (transkript.art === 'ok') kopiereTranskript(transkript.text);
            }}
          >
            Text kopieren
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={transkript.art !== 'ok' || transkript.segmente.length === 0}
            onClick={() => {
              if (transkript.art === 'ok' && transkriptFolge)
                ladeTranskriptHerunter(transkriptFolge, transkript.segmente);
            }}
          >
            Als .txt herunterladen
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{ minWidth: 280 }}
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default Replays;
